import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from './SuccessPage.module.css';
import OrderStatus from './OrderStatus';

interface SuccessPageProps {
  onCartRefresh: () => void;
}

const SuccessPage: React.FC<SuccessPageProps> = ({ onCartRefresh }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orderData, setOrderData] = useState<any>(null);

  useEffect(() => {
    // Get the actual purchased items before clearing the cart
    const purchasedItems = window.localStorage.getItem('cartItems');
    let orderItems = [];

    if (purchasedItems) {
      try {
        orderItems = JSON.parse(purchasedItems);
      } catch (error) {
        console.error('Error parsing cart items:', error);
        orderItems = [];
      }
    }

    // Get session ID from URL params
    const sessionId = searchParams.get('session_id');

    // Get email from localStorage
    const customerEmail = window.localStorage.getItem('checkoutEmail');

    // Debug: Log the email being retrieved
    console.log('Retrieved email from localStorage:', customerEmail);

    // Generate a clean, short order number (6 digits)
    const generateCleanOrderNumber = () => {
      const min = 100000; // 6 digits starting from 100000
      const max = 999999; // 6 digits up to 999999
      return Math.floor(Math.random() * (max - min + 1) + min).toString();
    };

    const cleanOrderNumber = generateCleanOrderNumber();

    // Debug: Log the generated order number
    console.log('Generated clean order number:', cleanOrderNumber);

    // Set order data with actual purchased items
    const finalOrderData = {
      id: cleanOrderNumber,
      status: 'paid',
      shippingStatus: 'pending',
      customerEmail: customerEmail || 'test@example.com',
      orderItems:
        orderItems.length > 0
          ? orderItems
          : [
              {
                name: 'Sample Product',
                quantity: 1,
                size: 'M',
                price: 29.99,
              },
              {
                name: 'Another Product',
                quantity: 2,
                size: 'L',
                price: 19.99,
              },
            ],
    };

    setOrderData(finalOrderData);

    // Debug: Log the final order data
    console.log('Final order data set:', finalOrderData);

    // Clear the cart from localStorage after successful purchase
    window.localStorage.setItem('cartItems', '[]');

    // Refresh the cart component and navbar number
    // Use setTimeout to ensure localStorage update is processed
    setTimeout(() => {
      onCartRefresh();
    }, 100);

    // Store order information for future reference
    if (customerEmail) {
      const orderInfo = {
        orderNumber: cleanOrderNumber,
        email: customerEmail,
        orderDate: new Date().toISOString(),
        items: orderItems,
        total: orderItems.reduce(
          (sum: number, item: any) => sum + item.price * item.quantity,
          0
        ),
        status: 'paid',
      };

      // Store in localStorage for persistence using clean order number
      window.localStorage.setItem(
        `order_${cleanOrderNumber}`,
        JSON.stringify(orderInfo)
      );

      // Store in orders list
      const existingOrders = window.localStorage.getItem('orders') || '[]';
      try {
        const orders = JSON.parse(existingOrders);
        orders.push(orderInfo);
        window.localStorage.setItem('orders', JSON.stringify(orders));
      } catch (error) {
        console.error('Error storing orders:', error);
      }

      console.log('Order stored successfully:', orderInfo);
    }

    // Clear the checkout email from localStorage
    if (customerEmail) {
      window.localStorage.removeItem('checkoutEmail');
    }
  }, [searchParams]);

  return (
    <div className={styles.successPage}>
      <div className={styles.successContainer}>
        {/* Success Icon and Title */}
        <div className={styles.successCard}>
          <div className={styles.successHeader}>
            <div className={styles.successIcon}>
              <svg
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className={styles.successTitle}>Payment Successful!</h1>
          </div>
          <p className={styles.successMessage}>
            Thank you for your purchase! Your payment has been processed
            successfully.
          </p>

          {/* Spam Disclaimer */}
          <div className={styles.spamDisclaimer}>
            <p className={styles.spamDisclaimerText}>
              📧 <strong>Important:</strong> Please check your spam/junk folder
              if you don't receive your order confirmation email within a few
              minutes.
            </p>
          </div>

          {/* Order Details */}
          {orderData && (
            <div className={styles.orderDetails}>
              <h3 className={styles.orderDetailsTitle}>Order Details:</h3>
              <p className={styles.orderDetailItem}>
                <span className={styles.orderDetailLabel}>Order Number:</span>{' '}
                {orderData.id}
              </p>
              {orderData.customerEmail && (
                <p className={styles.orderDetailItem}>
                  <span className={styles.orderDetailLabel}>Email:</span>{' '}
                  {orderData.customerEmail}
                </p>
              )}
              <p className={styles.orderDetailItem}>
                <span className={styles.orderDetailLabel}>Order Date:</span>{' '}
                {new Date().toLocaleDateString()}
              </p>
              <p className={styles.orderDetailItem}>
                <span className={styles.orderDetailLabel}>Confirmation:</span>{' '}
                Sent to your email
              </p>
              <p className={styles.orderDetailItem}>
                <span className={styles.orderDetailLabel}>Total Amount:</span> $
                {orderData.orderItems
                  .reduce(
                    (sum: number, item: any) =>
                      sum + item.price * item.quantity,
                    0
                  )
                  .toFixed(2)}
              </p>
            </div>
          )}

          <button
            onClick={() => navigate('/')}
            className={styles.continueShoppingButton}
          >
            Continue Shopping
          </button>
        </div>

        {/* Order Status */}
        {orderData && (
          <div className={styles.orderStatusSection}>
            <OrderStatus
              orderStatus={orderData.status}
              shippingStatus={orderData.shippingStatus}
              orderItems={orderData.orderItems}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SuccessPage;
